// Ambient pad voice for every front-end demo of the tool.
// Four oscillators per note through a shared lowpass into a damped feedback
// delay, so tones swell, bloom and overlap instead of plinking.
import { pitchToFrequency } from "@/lib/cryptogram";
import { logDebug, logWarn } from "@/lib/log";

let _ctx = null;
let _bus = null;

// iOS routes ordinary web audio through the "ambient" session, which the ringer
// switch silences. Declaring the session as playback (iOS 17+) puts us on the
// media channel instead, the way a music app behaves.
function claimPlaybackSession() {
  try {
    if ("audioSession" in navigator && navigator.audioSession.type !== "playback") {
      navigator.audioSession.type = "playback";
      return true;
    }
  } catch (err) {
    logDebug("audioSession unavailable", err);
  }
}

function getCtx() {
  if (!_ctx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    _ctx = new AC();
  }
  if (_ctx.state === "suspended") _ctx.resume();
  return _ctx;
}

// Designed wet levels for the two buses. `setReverbMix` scales both together,
// so 1 is exactly the room these voices were tuned in and the studio can dial
// it drier or wetter from there.
const PAD_WET = 0.33;
const PIANO_WET = 0.16;
let _reverbMix = 1;

export function setReverbMix(mix) {
  _reverbMix = Math.min(2, Math.max(0, Number(mix) || 0));
  if (_bus?.wet) _bus.wet.gain.value = PAD_WET * _reverbMix;
  if (_pianoBus?.wet) _pianoBus.wet.gain.value = PIANO_WET * _reverbMix;
  return _reverbMix;
}

/**
 * Ambient bus — built once, shared by every note so the tails pile up in the
 * same space.
 *
 *   input ─┬─→ dry 0.84 ───────────────────────────────→ destination
 *          └─→ delay 0.42s → lowpass 2200Hz ─┬─→ feedback 0.34 → delay
 *                                            └─→ wet 0.33 → destination
 *
 * A damped feedback delay, not a convolution reverb. The 2200 Hz damping in
 * the feedback path is what keeps the repeats from turning metallic.
 */
function getBus() {
  const ctx = getCtx();
  if (_bus) return _bus;

  const input = ctx.createGain();
  input.gain.value = 1;

  const dry = ctx.createGain();
  dry.gain.value = 0.84;

  const delay = ctx.createDelay(1.5);
  delay.delayTime.value = 0.42;

  const damp = ctx.createBiquadFilter();
  damp.type = "lowpass";
  damp.frequency.value = 2200;
  damp.Q.value = 0.4;

  const feedback = ctx.createGain();
  feedback.gain.value = 0.34;

  const wet = ctx.createGain();
  wet.gain.value = PAD_WET * _reverbMix;

  input.connect(dry);
  dry.connect(ctx.destination);

  input.connect(delay);
  delay.connect(damp);
  damp.connect(feedback);
  feedback.connect(delay);
  damp.connect(wet);
  wet.connect(ctx.destination);

  _bus = { ctx, input, wet };
  return _bus;
}

export function playPad(pitch, durationSec = 3.9, velocity = 0.5) {
  try {
    const { ctx, input } = getBus();
    const freq = pitchToFrequency(pitch);
    const t0 = ctx.currentTime;
    const v = Math.max(0.15, Math.min(1, velocity));
    const dur = Math.max(1.2, durationSec);
    const attack = Math.min(1.1, dur * 0.3);

    // Amplitude — no sustain plateau, no click. The swell IS the attack.
    const master = ctx.createGain();
    const peak = 0.2 * v;
    master.gain.setValueAtTime(0.0001, t0);
    master.gain.linearRampToValueAtTime(peak, t0 + attack);
    master.gain.exponentialRampToValueAtTime(
      Math.max(0.0002, peak * 0.55),
      t0 + attack + dur * 0.25
    );
    master.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

    // Filter opens as the note swells, then closes as it decays.
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.Q.value = 0.4;
    lp.frequency.setValueAtTime(Math.min(1200, freq * 2), t0);
    lp.frequency.linearRampToValueAtTime(Math.min(6500, freq * 6), t0 + attack * 1.4);
    lp.frequency.linearRampToValueAtTime(Math.min(2600, freq * 3), t0 + dur);

    // The breathing: a very slow detune drift on the two unison voices only.
    const lfo = ctx.createOscillator();
    lfo.type = "sine";
    lfo.frequency.value = 0.16;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 5; // cents
    lfo.connect(lfoGain);

    const voices = [
      { type: "triangle", mult: 1, detune: -5, gain: 0.5, drift: true },
      { type: "sine", mult: 1, detune: 6, gain: 0.34, drift: true },
      { type: "sine", mult: 2, detune: 0, gain: 0.1 + 0.06 * v, drift: false },
      { type: "sine", mult: 3, detune: 0, gain: 0.035, drift: false },
    ];
    const oscs = voices.map(({ type, mult, detune, gain, drift }) => {
      const o = ctx.createOscillator();
      o.type = type;
      o.frequency.value = freq * mult;
      o.detune.value = detune;
      if (drift) lfoGain.connect(o.detune);
      const g = ctx.createGain();
      g.gain.value = gain;
      o.connect(g);
      g.connect(lp);
      return o;
    });

    lp.connect(master);
    master.connect(input);

    const stopAt = t0 + dur + 0.1;
    lfo.start(t0);
    lfo.stop(stopAt);
    oscs.forEach((o) => {
      o.start(t0);
      o.stop(stopAt);
    });
  } catch (e) {
    // Audio not available — silent fail
    logWarn("synth error", e);
  }
}

// Kept as the generic entry point; the pad is the only voice we ship.
export function playPitch(pitch, durationSec, velocity) {
  playPad(pitch, durationSec, velocity);
}

let _pianoBus = null;

// A short, dark impulse response built from decaying noise — a real convolution
// tail instead of a delay slap. Keeps the piano in a room, not in a cave.
function makeImpulse(ctx, seconds = 1.5, decay = 3.2) {
  const len = Math.max(1, Math.floor(ctx.sampleRate * seconds));
  const buf = ctx.createBuffer(2, len, ctx.sampleRate);
  for (let ch = 0; ch < 2; ch += 1) {
    const data = buf.getChannelData(ch);
    let last = 0;
    for (let i = 0; i < len; i += 1) {
      const t = i / len;
      const white = Math.random() * 2 - 1;
      // One-pole lowpass on the noise so the tail is warm, not hissy.
      last = last * 0.72 + white * 0.28;
      data[i] = last * (1 - t) ** decay;
    }
  }
  return buf;
}

/**
 * Room for the piano voice: mostly dry, with a little convolution reverb
 * underneath so notes sit in a space without smearing the groove.
 */
function getPianoBus() {
  const ctx = getCtx();
  if (_pianoBus) return _pianoBus;

  const input = ctx.createGain();

  const dry = ctx.createGain();
  dry.gain.value = 0.88;

  const verb = ctx.createConvolver();
  verb.buffer = makeImpulse(ctx);
  const verbTone = ctx.createBiquadFilter();
  verbTone.type = "lowpass";
  verbTone.frequency.value = 3600;
  const wet = ctx.createGain();
  wet.gain.value = PIANO_WET * _reverbMix;

  input.connect(dry);
  dry.connect(ctx.destination);
  input.connect(verbTone);
  verbTone.connect(verb);
  verb.connect(wet);
  wet.connect(ctx.destination);

  _pianoBus = { ctx, input, wet };
  return _pianoBus;
}

/**
 * Struck-string voice. Each note is two slightly detuned "strings" (the beating
 * is what stops it sounding synthetic), each with inharmonic partials that die
 * off fast from the top down, plus a hammer thud. Attack is immediate, decay is
 * quicker up the keyboard — a jazz piano, not a pad.
 */
export function playPiano(pitch, durationSec = 1.6, velocity = 0.6) {
  try {
    const { ctx, input } = getPianoBus();
    const freq = pitchToFrequency(pitch);
    const t0 = ctx.currentTime;
    const v = Math.max(0.15, Math.min(1, velocity));
    // Bass strings ring; the top of the keyboard doesn't.
    const decayScale = Math.max(0.5, Math.min(2.2, 200 / freq + 0.5));
    const dur = Math.max(0.35, Math.min(durationSec, 2.6 * decayScale));

    const master = ctx.createGain();
    const peak = 0.3 * v;
    master.gain.setValueAtTime(0.0001, t0);
    master.gain.linearRampToValueAtTime(peak, t0 + 0.004);
    // Fast initial drop, then a long quiet ring — the shape of a real string.
    master.gain.exponentialRampToValueAtTime(peak * 0.22, t0 + 0.09 * decayScale);
    master.gain.exponentialRampToValueAtTime(peak * 0.05, t0 + 0.45 * decayScale);
    master.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.Q.value = 0.5;
    // Struck hard = bright, and the brightness dies away within the first beat.
    lp.frequency.setValueAtTime(Math.min(11000, freq * (7 + 10 * v)), t0);
    lp.frequency.exponentialRampToValueAtTime(Math.max(700, freq * 2.6), t0 + 0.35);
    lp.frequency.exponentialRampToValueAtTime(Math.max(420, freq * 1.6), t0 + dur);

    // Partial gains fall away steeply — a plucked/struck spectrum, not an organ.
    const partials = [
      { mult: 1, gain: 0.42, life: 1.0 },
      { mult: 2.001, gain: 0.2, life: 0.5 },
      { mult: 3.005, gain: 0.1, life: 0.3 },
      { mult: 4.012, gain: 0.05 + 0.03 * v, life: 0.18 },
      { mult: 5.02, gain: 0.02 * v, life: 0.1 },
      { mult: 6.04, gain: 0.012 * v, life: 0.07 },
      { mult: 8.07, gain: 0.006 * v, life: 0.05 },
    ];
    const oscs = [];
    // Two strings per note, a couple of cents apart, for the beating.
    [-3.5, 3.5].forEach((stringDetune, si) => {
      partials.forEach(({ mult, gain, life }) => {
        const o = ctx.createOscillator();
        o.type = "sine";
        o.frequency.value = freq * mult;
        o.detune.value = stringDetune * mult;
        const g = ctx.createGain();
        const level = gain * (si === 0 ? 0.62 : 0.38);
        const tail = Math.max(0.08, dur * life);
        g.gain.setValueAtTime(level, t0);
        g.gain.exponentialRampToValueAtTime(Math.max(0.00005, level * 0.015), t0 + tail);
        o.connect(g);
        g.connect(lp);
        oscs.push({ o, stopAt: t0 + tail + 0.05 });
      });
    });

    // Hammer: a 30 ms filtered thud, the part your ear reads as "piano".
    const noiseLen = 0.03;
    const buf = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * noiseLen), ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / data.length) ** 2;
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const noiseBp = ctx.createBiquadFilter();
    noiseBp.type = "bandpass";
    noiseBp.frequency.value = Math.min(3800, Math.max(180, freq * 2.2));
    noiseBp.Q.value = 0.7;
    const noiseGain = ctx.createGain();
    noiseGain.gain.value = 0.085 * v;
    noise.connect(noiseBp);
    noiseBp.connect(noiseGain);
    noiseGain.connect(lp);

    lp.connect(master);
    master.connect(input);

    noise.start(t0);
    oscs.forEach(({ o, stopAt }) => {
      o.start(t0);
      o.stop(stopAt);
    });
  } catch (e) {
    logWarn("piano synth error", e);
  }
}

/**
 * Plucked string — the harp voice. Bright, immediate attack (a nail on the
 * string, not a hammer), a harmonic spectrum that thins out as it rings, and a
 * long decay that's longer on the low strings. Shares the piano's dry-ish room.
 */
export function playHarp(pitch, durationSec = 2.6, velocity = 0.6) {
  try {
    const { ctx, input } = getPianoBus();
    const freq = pitchToFrequency(pitch);
    const t0 = ctx.currentTime;
    const v = Math.max(0.12, Math.min(1, velocity));
    // Bass strings ring for a long time; the top of the harp doesn't.
    const decayScale = Math.max(0.45, Math.min(2.4, 260 / freq + 0.45));
    const dur = Math.max(0.5, Math.min(durationSec * decayScale, 7));

    const master = ctx.createGain();
    const peak = 0.26 * v;
    master.gain.setValueAtTime(0.0001, t0);
    master.gain.linearRampToValueAtTime(peak, t0 + 0.003);
    master.gain.exponentialRampToValueAtTime(peak * 0.42, t0 + 0.12 * decayScale);
    master.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);

    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.Q.value = 0.4;
    lp.frequency.setValueAtTime(Math.min(13000, freq * (9 + 8 * v)), t0);
    lp.frequency.exponentialRampToValueAtTime(Math.max(800, freq * 3), t0 + 0.5);
    lp.frequency.exponentialRampToValueAtTime(Math.max(400, freq * 1.5), t0 + dur);

    // A harmonic series — a harp string is far less inharmonic than a piano's.
    const partials = [
      { mult: 1, gain: 0.5, life: 1.0 },
      { mult: 2, gain: 0.26, life: 0.62 },
      { mult: 3, gain: 0.13, life: 0.4 },
      { mult: 4, gain: 0.07, life: 0.26 },
      { mult: 5, gain: 0.04 * v, life: 0.16 },
      { mult: 6, gain: 0.025 * v, life: 0.11 },
      { mult: 7, gain: 0.015 * v, life: 0.08 },
    ];
    const oscs = [];
    partials.forEach(({ mult, gain, life }) => {
      const o = ctx.createOscillator();
      o.type = "sine";
      o.frequency.value = freq * mult;
      // A touch of detune so the string breathes instead of sitting still.
      o.detune.value = (mult % 2 ? 1.2 : -1.6) * mult;
      const g = ctx.createGain();
      const tail = Math.max(0.12, dur * life);
      g.gain.setValueAtTime(gain, t0);
      g.gain.exponentialRampToValueAtTime(Math.max(0.00005, gain * 0.01), t0 + tail);
      o.connect(g);
      g.connect(lp);
      oscs.push({ o, stopAt: t0 + tail + 0.05 });
    });

    // The nail: a 12 ms bright tick that sells the pluck.
    const noiseLen = 0.012;
    const buf = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * noiseLen), ctx.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i += 1) {
      data[i] = (Math.random() * 2 - 1) * (1 - i / data.length) ** 1.6;
    }
    const noise = ctx.createBufferSource();
    noise.buffer = buf;
    const noiseHp = ctx.createBiquadFilter();
    noiseHp.type = "bandpass";
    noiseHp.frequency.value = Math.min(6500, Math.max(500, freq * 4));
    noiseHp.Q.value = 0.9;
    const noiseGain = ctx.createGain();
    noiseGain.gain.value = 0.06 * v;
    noise.connect(noiseHp);
    noiseHp.connect(noiseGain);
    noiseGain.connect(lp);

    lp.connect(master);
    master.connect(input);

    noise.start(t0);
    oscs.forEach(({ o, stopAt }) => {
      o.start(t0);
      o.stop(stopAt);
    });
  } catch (e) {
    logWarn("harp synth error", e);
  }
}

export function playDing(velocity = 0.6) {
  try {
    const { ctx, input } = getBus();
    const now = ctx.currentTime;
    const v = Math.max(0.1, Math.min(1, velocity));
    const master = ctx.createGain();
    master.gain.setValueAtTime(0.0001, now);
    master.gain.linearRampToValueAtTime(0.2 * v, now + 0.05);
    master.gain.exponentialRampToValueAtTime(0.0001, now + 2.6);
    master.connect(input);
    [
      { f: 1568, g: 0.5, type: "sine", d: -5 },
      { f: 3136, g: 0.16, type: "sine", d: 6 },
      { f: 4704, g: 0.05, type: "sine", d: 0 },
    ].forEach(({ f, g, type, d }) => {
      const o = ctx.createOscillator();
      o.type = type;
      o.frequency.value = f;
      o.detune.value = d;
      const gn = ctx.createGain();
      gn.gain.value = g;
      o.connect(gn);
      gn.connect(master);
      o.start(now);
      o.stop(now + 2.7);
    });
  } catch (e) {
    logDebug("ding failed", e);
  }
}

/**
 * Call from a user gesture before playing anything. Builds the bus and, on iOS,
 * claims a playback audio session so the ringer switch doesn't mute us.
 */
export function unlockAudio() {
  try {
    claimPlaybackSession();
    getBus();
  } catch (err) {
    logDebug("audio unlock failed", err);
  }
}
